import type { Dispatcher } from "undici";

import { log } from "@voxmedia/duet-utils/logging";

/**
 * Reads an integer override from the environment, falling back to the default
 * for anything outside `[min, max]` — a mistyped deployment value included.
 *
 * Strict on purpose: both knobs below fail destructively on `NaN`, and in
 * opposite directions that are each worse than not having the knob at all. As a
 * retry budget it makes `attempt >= retries` permanently false, so a retryable
 * failure retries forever. As a connect timeout it is falsy to undici, whose
 * `setupConnectTimeout` then installs no timer at all, leaving connects
 * unbounded rather than capped at the 10s default.
 *
 * The upper bounds guard the same outcomes from the other side, where one extra
 * zero would otherwise be taken literally: runaway retries amplify load on an
 * origin that is by definition already failing, and an over-long timeout holds
 * sockets open rather than releasing them.
 */
function readIntEnv(
  raw: string | undefined,
  fallback: number,
  { min, max }: { min: number; max: number },
): number {
  if (!raw?.trim()) {
    return fallback;
  }

  const parsed = Number(raw);

  return Number.isSafeInteger(parsed) && parsed >= min && parsed <= max
    ? parsed
    : fallback;
}

/**
 * Transport configuration for outbound wp-api GraphQL requests.
 *
 * SSR fetches the community's own public hostname through Fastly. A small
 * fraction of those TCP handshakes are black-holed: the SYN leaves the node and
 * nothing comes back, so undici gives up at its default `connectTimeout` of
 * 10s and the request boundary turns the rejection into a 500. Connect latency
 * is otherwise p50 8ms / p99 109ms, so the default budget only prolongs
 * failures that were never going to succeed.
 *
 * These knobs bound the connect phase only. Response time stays unbounded, so a
 * legitimately slow GraphQL query is never aborted.
 *
 * undici rounds this up on a coarse timer, so the effective cutoff is roughly
 * this value plus 500ms — 2000 here gives up at ~2.5s. That skew is also why
 * the default 10000 shows up in APM as 10.4–10.7s spans. ~2.5s stays well clear
 * of both the p99 connect of 109ms and the ~1.01s population that recovers on
 * the origin's SYN-ACK retransmit, while cutting the 10s failures short.
 */
export const WP_API_CONNECT_TIMEOUT_MS = readIntEnv(
  process.env.WP_API_CONNECT_TIMEOUT_MS,
  2000,
  // Past ~30s a connect budget is worse than undici's own default: it stops
  // bounding anything and just holds the socket.
  { min: 1, max: 30_000 },
);

/**
 * Extra attempts after the first. A black-holed path is not recovered by the
 * kernel's SYN retransmits on the same 5-tuple — only a new socket, and so a
 * new source port, gets around it. A fresh `fetch` gives us that, and in the
 * captured production trace the retry connected in 11ms.
 */
export const WP_API_CONNECT_RETRIES = readIntEnv(
  process.env.WP_API_CONNECT_RETRIES,
  1,
  // A handful of attempts is the most that can still be called recovery;
  // beyond that we are amplifying load on a failing origin.
  { min: 0, max: 5 },
);

/**
 * Error codes that prove the request never reached the origin, so replaying it
 * cannot duplicate a side effect. Deliberately excludes mid-flight failures
 * such as `ECONNRESET`, which can land after bytes were already sent.
 */
const RETRYABLE_CONNECT_ERROR_CODES = new Set([
  "UND_ERR_CONNECT_TIMEOUT",
  "ECONNREFUSED",
  "ENETUNREACH",
  "EHOSTUNREACH",
]);

/**
 * Node's `fetch` rejects with a bare `TypeError: fetch failed` and hangs the
 * real error off `cause`, so the code we care about is one level down.
 */
export function isRetryableConnectError(err: unknown): boolean {
  const cause = (err as { cause?: unknown } | undefined)?.cause;
  const code = (cause as { code?: unknown } | undefined)?.code;

  return typeof code === "string" && RETRYABLE_CONNECT_ERROR_CODES.has(code);
}

let dispatcher: Promise<Dispatcher | undefined> | undefined;

/**
 * Lazily builds the process-wide undici `Agent` used for wp-api requests, and
 * its connection pool with it, on the first server-side GraphQL call.
 *
 * `undici` is imported dynamically rather than at the top of the module on
 * purpose. `_app.tsx` imports the isomorphic query fetcher, so
 * `createFetcher` is part of the client webpack graph; a static import would
 * drag `node:net`/`node:tls` into the browser and edge bundles. Resolves to
 * `undefined` off the Node runtime, where the caller leaves `fetch` untouched.
 *
 * A failed load resolves `undefined` rather than rejecting. Every GraphQL
 * request awaits this, so letting it throw would turn an unloadable undici into
 * a total outage. Degrading to the default connect timeout and no retry is the
 * behavior we had before this existed.
 *
 * That fallback is silent, which is why `undici` is in `transpilePackages` in
 * `next.config.base.ts`: the bundler must inline it here. Left external, the
 * specifier survives into the server chunk, the standalone output has no
 * `node_modules/undici` for Node to resolve, and every request lands in the
 * catch below — shipping the feature switched off rather than failing loudly.
 */
export function getWpApiDispatcher(): Promise<Dispatcher | undefined> {
  dispatcher ??= (async () => {
    if (typeof window !== "undefined" || process.env.NEXT_RUNTIME === "edge") {
      return undefined;
    }

    try {
      // Plain specifier on purpose — a `webpackIgnore`/`turbopackIgnore` hint
      // here would defeat the `transpilePackages` entry that inlines this.
      const { Agent } = await import("undici");

      return new Agent({ connect: { timeout: WP_API_CONNECT_TIMEOUT_MS } });
    } catch (err) {
      // Memoised, so this logs once per process rather than per request.
      log.warn(
        { err },
        "Could not load undici; wp-api requests fall back to the default connect timeout with no retry",
      );

      return undefined;
    }
  })();

  return dispatcher;
}
